import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '../../MuiComponents/Modal';
import { MuiTextField } from '../../MuiComponents/FormInputs';
import Info from '../../Info';
import Button from '../../Button';
import Typography from '../../Typography';
import { AlertContext } from '../../SuccessOrFailedModal';
import { email as emailValidation, required_message } from '../../../utils/validations';
import { useInviteMember } from '../../../hooks/members';
import { getGrantableRoles } from '../../../utils/constants/inviteGrantMatrix';
import { accessModes, platformRoles } from '../../../utils/constants/platformRoles';
import { splitFullName } from '../Profile';
import Storage from '../../../utils/Storage';
import { useForm } from 'react-hook-form';

/**
 * ROAR-4970 (ROAR-4802 / P1.11, PR3) — the Invite Teammate modal (AC1, AC10, AC12, AC15).
 *
 * Visual redesign (developer-provided mock) — a plain white header (title + subtitle + divider)
 * rather than the shared `Modal` component's own `dialogTitle`/`subtitle` props, which paint a
 * fixed gradient background (`mui_overrides.scss`'s `.MuiDialogTitle-root` rule) meant for other
 * modals in the app; building the header directly in `children` avoids fighting that global style.
 * Everything here is also wrapped in a `text-left` block: every modal's `.MuiPaper-root` is
 * globally `text-align: center` (`mui_overrides.scss`), an established convention this mock
 * deliberately breaks from. Every `MuiTextField` also globally forces a `--primary-500` (teal)
 * border regardless of focus state (same file); `className="grey2"` is that override system's own
 * built-in escape hatch for a plain gray border, not a one-off hack.
 *
 * A single FULL NAME field, split into `first_name`/`last_name` via `splitFullName` — the same
 * helper and convention Settings/Profile's own Figma-matched name field already established, reused
 * rather than duplicated.
 *
 * Role/access options are the caller's own grantable pairs from `inviteGrantMatrix` (hardcoded
 * client-side, developer-confirmed during /plan) — never a full role list. For an mssp_admin,
 * the target company is whichever one is currently selected via the existing "Selected Customer"
 * dropdown (no company picker here, per this sub-task's own scope-out); `hasTargetCompany` lets
 * the parent tell this modal whether that selection currently exists, so the guard can render
 * before a submit ever reaches the backend's own 403 for the same condition.
 *
 * Every refusal (seat cap AC10, non-leaking conflict AC12, invalid email/name, ungrantable role)
 * is displayed by showing the backend's own message verbatim — this codebase's established
 * convention (e.g. SignUp/OtpStep.js) — rather than reimplementing per-cause copy client-side.
 */
const ROLE_LABELS = {
  customer_admin: 'Company Admin',
  customer_user: 'Company User',
};

// "Read-Only" is confirmed as a UI label only in siemba-billing-db-design.html (the DB column
// itself never holds anything but write|read) — "Full Access" for write isn't equally confirmed
// there, but matches the design mock's own consistent usage for this column.
const ACCESS_LABELS = {
  [accessModes.WRITE]: 'Full Access',
  [accessModes.READ]: 'Read-Only',
};

const FIELD_LABEL_CLASS =
  'block text-xs font-bold uppercase tracking-wide text-[rgba(0,0,0,0.6)] mb-1';

const InviteTeammateModal = ({ open, onClose, callerPlatformRole, hasTargetCompany }) => {
  const [popup] = useContext(AlertContext);
  const invite = useInviteMember();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm({ mode: 'onChange' });

  const grantableRoles = getGrantableRoles(callerPlatformRole);
  const roleOptions = [...new Set(grantableRoles.map((g) => g.platform_role))];
  const accessOptions = [...new Set(grantableRoles.map((g) => g.access_mode))];

  const selectedRole = watch('platform_role') || '';
  const selectedAccess = watch('access_mode') || '';
  const selectedEmail = watch('email') || '';

  const isSubmitDisabled =
    !hasTargetCompany || !selectedEmail || !selectedRole || !selectedAccess || invite.isLoading;

  const close = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data) => {
    try {
      const { first_name, last_name } = splitFullName(data.full_name);
      await invite.mutateAsync({
        email: data.email,
        first_name: first_name || undefined,
        last_name: last_name || undefined,
        platform_role: data.platform_role,
        access_mode: data.access_mode,
      });
      // An mssp_admin invites INTO the selected company (AC15), but GET /team/members always
      // returns the MSSP's own staff roster, never a managed company's (resolveCompanyScope.js —
      // deliberate backend design, not a bug) — so the invitee will never show up in this list.
      // Without this, the generic toast plus an unchanged list reads as a silent failure.
      const description =
        callerPlatformRole === platformRoles.MSSP_ADMIN
          ? `${data.email} won't appear in this list — they'll show up in ${
              Storage.getStoredCompanyName() || "the selected company"
            }'s own team once a company-scoped view exists.`
          : undefined;
      popup({ variant: 'success', title: 'Invitation sent', description, okayText: 'Okay' });
      close();
    } catch (err) {
      popup({
        variant: 'error',
        title: 'Invitation failed',
        description: err?.response?.data?.message || 'Something went wrong. Please try again.',
        okayText: 'Okay',
      });
    }
  };

  return (
    <Modal open={open} onClose={close} maxWidth={'xs'} fullWidth={true} hideCloseIcon={true}>
      <div className={'text-left'}>
        <div className={'relative pr-6'}>
          <CloseIcon
            onClick={close}
            className={'absolute right-0 top-0 cursor-pointer text-[rgba(0,0,0,0.6)]'}
          />
          <Typography variant={'h5'} className={'font-bold'}>
            Invite teammate
          </Typography>
          <Typography variant={'body2'} className={'text-[rgba(0,0,0,0.6)] mt-1'}>
            They&apos;ll receive an email with a link to join your workspace.
          </Typography>
        </div>
        <hr className={'my-3 border-grey2'} />

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {!hasTargetCompany && (
            <Typography variant={'body2'} className={'text-functional-red mb-3'}>
              Select a customer from the dropdown above before inviting a teammate.
            </Typography>
          )}

          <div className={'mb-2'}>
            <label className={FIELD_LABEL_CLASS}>Email address</label>
            <MuiTextField
              className={'grey2'}
              margin={'none'}
              placeholder={'colleague@company.com'}
              InputProps={{
                ...register('email', { required: required_message, ...emailValidation }),
              }}
              error={Boolean(errors?.email?.message)}
              helperText={errors?.email?.message}
            />
          </div>

          <div className={'mb-2'}>
            <label className={FIELD_LABEL_CLASS}>Full name</label>
            <MuiTextField
              className={'grey2'}
              margin={'none'}
              placeholder={'Jane Smith'}
              InputProps={{ ...register('full_name') }}
            />
          </div>

          <div className={'mb-2'}>
            <label className={FIELD_LABEL_CLASS}>
              Role <Info tooltip={'What this teammate is allowed to do in the account.'} />
            </label>
            <Select
              displayEmpty
              fullWidth
              size={'small'}
              inputProps={{ 'aria-label': 'Role' }}
              value={selectedRole}
              onChange={(e) => setValue('platform_role', e.target.value, { shouldValidate: true })}
              renderValue={(value) => (value ? ROLE_LABELS[value] || value : 'Choose a role')}
            >
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {ROLE_LABELS[role] || role}
                </MenuItem>
              ))}
            </Select>
          </div>

          <div className={'mb-2'}>
            <label className={FIELD_LABEL_CLASS}>
              Access <Info tooltip={'Write can make changes; Read can only view.'} />
            </label>
            <Select
              displayEmpty
              fullWidth
              size={'small'}
              inputProps={{ 'aria-label': 'Access' }}
              value={selectedAccess}
              onChange={(e) => setValue('access_mode', e.target.value, { shouldValidate: true })}
              renderValue={(value) => (value ? ACCESS_LABELS[value] || value : 'Choose access')}
            >
              {accessOptions.map((mode) => (
                <MenuItem key={mode} value={mode}>
                  {ACCESS_LABELS[mode] || mode}
                </MenuItem>
              ))}
            </Select>
          </div>

          <hr className={'my-3 border-grey2'} />
          <div className={'flex justify-end gap-3'}>
            <Button type={'button'} variant={'outlined'} onClick={close}>
              Cancel
            </Button>
            <Button disabled={isSubmitDisabled}>
              {invite.isLoading ? 'Inviting...' : 'Invite'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

InviteTeammateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  callerPlatformRole: PropTypes.string,
  hasTargetCompany: PropTypes.bool,
};

export default InviteTeammateModal;
